import { Inject, Injectable, OnDestroy, Optional } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { BASE_PATH, Configuration, GrundbuchmeldungSearchRequest, ZipRequest } from "../gb-api";
import { Observable, Subscription } from "rxjs";
import { AuszugInfoItem } from "../../shared/components/auszug/auszug-print-service";
import { TranslateService } from "@ngx-translate/core";
import { TimepointService } from "./timepoint.service";
import { sanatizedSaveAs } from "../../shared/functions/file.functions";
import { AuszugPrintOptions } from "../../shared/components/auszug/settings/auszug-settings-service";
import { CapAlertService } from "../extension/exceptions/cap-alert.service";
import { tap } from "rxjs/operators";

@Injectable()
export class GbPrintService implements OnDestroy {

  protected basePath = 'http://localhost:8080';
  public defaultHeaders = new HttpHeaders();
  public configuration = new Configuration();

  private subscription: Subscription = new Subscription();
  zipInProgressMap = new Map<number, boolean>([]);
  printInProgressMap = new Map<number, string>([]);


  constructor(protected httpClient: HttpClient,
              private translate: TranslateService,
              private timepointService: TimepointService,
              private capAlertService: CapAlertService,
              @Optional() @Inject(BASE_PATH) basePath: string,
              @Optional() configuration: Configuration) {
    if (basePath) {
      this.basePath = basePath;
    }
    if (configuration) {
      this.configuration = configuration;
      this.basePath = basePath || configuration.basePath || this.basePath;
    }
  }


  static print(data: any[], filename: string, propertyBag: FilePropertyBag) {
    let file;
    try {
      file = new File(data, filename, propertyBag);
    } catch (e) {
      // IE 11 does not know File API
      file = new Blob(data, propertyBag);
      file.name = filename;
      file.lastModifiedDate = new Date();
      file.lastModified += file.lastModifiedDate;
    }
    sanatizedSaveAs(file);
  }

  static getCurrentDate(): string {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();
    return mm + '_' + dd + '_' + yyyy;
  }

  /**
   * Gibt einen Rechte Auszug im PDF format zurück.
   *
   */
  public getRechteAuszugPDF(auszugItem: AuszugInfoItem, indexNumber: number) {
    this.subscription.add(this.getRechtPDF(auszugItem.id, auszugItem.art, indexNumber).subscribe(
      data => {
        GbPrintService.print([data],
          auszugItem.filePrefix + " " + auszugItem.art + ".pdf",
          {type: "application/pdf"});
      }
    ));
  }

  /**
   * Gibt einen Rechte Auszug im RTF format zurück.
   *
   */
  public getRechteAuszugRTF(auszugItem: AuszugInfoItem, indexNumber: number) {
    this.subscription.add(this.getRechtRTF(auszugItem.id, auszugItem.art, indexNumber).subscribe(
      data => {
        GbPrintService.print([data],
          auszugItem.filePrefix + " " + auszugItem.art + ".rtf",
          {type: "application/rtf"});
      }
    ));
  }

  public zipGrundstueckAuszuege(printOptions: AuszugPrintOptions) {
    if (!this.zipInProgressMap.has(printOptions.indexNumber)) {
      this.zipInProgressMap.set(printOptions.indexNumber, true);
    }
    this.subscription.add(this.downloadGrundstueckeZip(printOptions).subscribe(
      data => {
        GbPrintService.print([data], "Grundstueckauszuege_" + GbPrintService.getCurrentDate() + ".zip", {type: "application/zip"});
      }, _ => {
        this.capAlertService.info(this.translate.instant("KeineBerechtigungFürDieseAktion"));
      }
    ).add(() => this.zipInProgressMap.delete(printOptions.indexNumber)));
  }

  public zipRechtAuszuege(printOptions: AuszugPrintOptions) {
    if (!this.zipInProgressMap.has(printOptions.indexNumber)) {
      this.zipInProgressMap.set(printOptions.indexNumber, true);
    }
    this.subscription.add(this.downloadRechteZip(printOptions).subscribe(
      data => {
        GbPrintService.print([data], "Rechtauszuege_" + GbPrintService.getCurrentDate() + ".zip", {type: "application/zip"});
      }, _ => {
        this.capAlertService.info(this.translate.instant("KeineBerechtigungFürDieseAktion"));
      }
    ).add(() => this.zipInProgressMap.delete(printOptions.indexNumber)));
  }

  public zipEigentuemerAuszuege(printOptions: AuszugPrintOptions) {
    if (!this.zipInProgressMap.has(printOptions.indexNumber)) {
      this.zipInProgressMap.set(printOptions.indexNumber, true);
    }
    this.subscription.add(this.downloadEigentuemerZip(printOptions).subscribe(
      data => {
        GbPrintService.print([data], "Eigentuemerauszuege_" + GbPrintService.getCurrentDate() + ".zip", {type: "application/zip"});
      }, _ => {
        this.capAlertService.info(this.translate.instant("KeineBerechtigungFürDieseAktion"));
      }
    ).add(() => this.zipInProgressMap.delete(printOptions.indexNumber)));
  }

  /**
   * Gibt einen Grundstueck Auszug im PDF format zurück.
   *
   */
  public getGrundstueckAuszugPDF(auszugItem: AuszugInfoItem, indexNumber: number) {
    if (!this.printInProgressMap.has(indexNumber)) {
      this.printInProgressMap.set(indexNumber, 'PDF');
    }
    this.subscription.add(this.getGrundstueckPDF(auszugItem.id).subscribe(
      data => {
        GbPrintService.print([data], auszugItem.filePrefix + ".pdf", {type: "application/pdf"});
      }, _ => {
        this.capAlertService.info(this.translate.instant("KeineBerechtigungFürDieseAktion"));
      }
    ).add(() => {
      this.printInProgressMap.delete(indexNumber);
    }));
  }

  /**
   * Gibt einen Grundstueck Auszug im RTF format zurück.
   *
   */
  public getGrundstueckAuszugRTF(auszugItem: AuszugInfoItem, indexNumber: number) {
    if (!this.printInProgressMap.has(indexNumber)) {
      this.printInProgressMap.set(indexNumber, 'RTF');
    }
    this.subscription.add(this.getGrundstueckRTF(auszugItem.id).subscribe(
      data => {
        GbPrintService.print([data],
          auszugItem.filePrefix + ".rtf",
          {type: "application/rtf"});
      }
    ).add(() => {
      this.printInProgressMap.delete(indexNumber);
    }));
  }

  /**
   * Gibt einen Eigentuemer Auszug im PDF format zurück.
   *
   */
  public getEigentuemerAuszugPDF(auszugItem: AuszugInfoItem) {
    this.subscription.add(this.getEigentuemerPDF(auszugItem.id).subscribe(
      data => {
        GbPrintService.print([data],
          auszugItem.filePrefix + "_" + auszugItem.art + ".pdf",
          {type: "application/pdf"});
      }));
  }

  /**
   * Gibt einen Eigentuemer Auszug im RTF format zurück.
   *
   */
  public getEigentuemerAuszugRTF(auszugItem: AuszugInfoItem) {
    this.subscription.add(this.getEigentuemerRTF(auszugItem.id).subscribe(
      data => {
        GbPrintService.print([data],
          auszugItem.filePrefix + "_" + auszugItem.art + ".rtf",
          {type: "application/rtf;"});
      }
    ));
  }

  /**
   * Gibt einen Eigentuemer Auszug im PDF format zurück.
   *
   */
  public getGrundbuchmeldungAuszugPDF(auszugItem: AuszugInfoItem, indexNumber: number) {
    if (!this.printInProgressMap.has(indexNumber)) {
      this.printInProgressMap.set(indexNumber, 'PDF');
    }
    this.subscription.add(this.getGrundbuchmeldungPDF(auszugItem.id).subscribe(
      data => {
        GbPrintService.print([data],
          auszugItem.filePrefix + "_" + auszugItem.art + ".pdf",
          {type: "application/pdf;"});
      }, _ => {
        this.capAlertService.info(this.translate.instant("KeineBerechtigungFürDieseAktion"));
      }
    ).add(() => {
      this.printInProgressMap.delete(indexNumber);
    }));
  }

  /**
   * Gibt einen Eigentuemer Auszug im PDF format zurück.
   *
   */
  public getGrundbuchmeldungAuszugRTF(auszugItem: AuszugInfoItem, indexNumber: number) {
    if (!this.printInProgressMap.has(indexNumber)) {
      this.printInProgressMap.set(indexNumber, 'RTF');
    }
    this.subscription.add(this.getGrundbuchmeldungRTF(auszugItem.id).subscribe(
      data => {
        GbPrintService.print([data],
          auszugItem.filePrefix + "_" + auszugItem.art + ".rtf",
          {type: "application/rtf;"});
      }
    ).add(() => {
      this.printInProgressMap.delete(indexNumber);
    }));
  }

  public downloadGrundbuchmeldungenZip(request: GrundbuchmeldungSearchRequest): any {
    if (request === null || request === undefined) {
      throw new Error('Required parameter request was null or undefined when calling downloadZip.');
    }
    return this.httpClient.post(`${this.basePath}/gb/grundbuchmeldung/grundbuchmeldungZip/${encodeURIComponent(String(this.translate.currentLang))}`, request,
      {
        responseType: 'blob',
        withCredentials: this.configuration.withCredentials,
      });
  }

  private getEigentuemerPDF(personStammMasterId: string): Observable<Blob> {
    if (personStammMasterId === null || personStammMasterId === undefined
    ) {
      throw new Error('Required parameter personStammVersionId was null or undefined when calling getEigentuemerAuszugPDF.');
    }
    const headers = this.defaultHeaders;
    return this.httpClient.get(`${this.basePath}/gb/print/eigentuemerAuszugPDF/${encodeURIComponent(String(personStammMasterId))}/${encodeURIComponent(String(this.translate.currentLang))}`,
      {
        headers: headers,
        responseType: 'blob',
        withCredentials: this.configuration.withCredentials,
      }
    );
  }

  private getEigentuemerRTF(personStammMasterId: string): Observable<Blob> {
    if (personStammMasterId === null || personStammMasterId === undefined
    ) {
      throw new Error('Required parameter personStammVersionId was null or undefined when calling getEigentuemerAuszugRTF.');
    }
    const headers = this.defaultHeaders;
    return this.httpClient.get(`${this.basePath}/gb/print/eigentuemerAuszugRTF/${encodeURIComponent(String(personStammMasterId))}/${encodeURIComponent(String(this.translate.currentLang))}`,
      {
        headers: headers,
        responseType: 'blob',
        withCredentials: this.configuration.withCredentials,
      }
    );
  }

  private getRechtRTF(rechteversionsId: string, rechtArt: string, indexNumber: number): any {
    if (rechteversionsId === null || rechteversionsId === undefined) {
      throw new Error('Required parameter request was null or undefined when calling getAuszugRTF.');
    }
    if (!this.printInProgressMap.has(indexNumber)) {
      this.printInProgressMap.set(indexNumber, 'RTF');
    }
    const headers = this.defaultHeaders;

    return this.httpClient
      .get(`${this.basePath}/gb/print/rechteAuszugRTF/${encodeURIComponent(String(rechteversionsId))}` +
        `/${encodeURIComponent(String(rechtArt))}/${encodeURIComponent(String(this.translate.currentLang))}`,
        {
          headers: headers,
          responseType: 'blob',
          withCredentials: this.configuration.withCredentials,
        }).pipe(
        tap(() => {
            this.printInProgressMap.delete(indexNumber);
          }
        ));
  }

  private getRechtPDF(rechteversionsId: string, rechtArt: string, indexNumber: number): any {
    if (rechteversionsId === null || rechteversionsId === undefined) {
      throw new Error('Required parameter rechteversionsId was null or undefined when calling getAuszugPDF.');
    }
    if (!this.printInProgressMap.has(indexNumber)) {
      this.printInProgressMap.set(indexNumber, 'PDF');
    }
    const headers = this.defaultHeaders;

    return this.httpClient
      .get(`${this.basePath}/gb/print/rechteAuszugPDF/${encodeURIComponent(String(rechteversionsId))}` +
        `/${encodeURIComponent(String(rechtArt))}/${encodeURIComponent(String(this.translate.currentLang))}`,
        {
          headers: headers,
          responseType: 'blob',
          withCredentials: this.configuration.withCredentials,
        }).pipe(
        tap(() => {
            this.printInProgressMap.delete(indexNumber);
          }
        ));
  }

  public getExportEigentuemerXls(mitGebaeude: boolean, sprache: string): any {
    const headers = this.defaultHeaders;

    return this.httpClient
      .get(`${this.basePath}/gb/print/exportEigentuemer/`,
        {
          params: {"mitgebaeude": mitGebaeude.toString(), "lang": sprache},
          headers: headers,
          responseType: 'blob',
          withCredentials: this.configuration.withCredentials,
        });
  }

  private downloadGrundstueckeZip(printOptions: AuszugPrintOptions) {
    return this.downloadZip(printOptions, `${this.basePath}/gb/print/grundstueckAuszuge/zip`);
  }

  private downloadRechteZip(printOptions: AuszugPrintOptions) {
    return this.downloadZip(printOptions, `${this.basePath}/gb/print/rechtAuszuge/zip`);
  }

  private downloadEigentuemerZip(printOptions: AuszugPrintOptions) {
    return this.downloadZip(printOptions, `${this.basePath}/gb/print/eigentuemerAuszuge/zip`);
  }

  private downloadZip(printOptions: AuszugPrintOptions, path: string) {
    let ids = printOptions.ids;
    if (ids === null || ids === undefined) {
      throw new Error('Required parameter request was null or undefined when calling downloadZip.');
    }
    const zipRequest: ZipRequest = {
      ids: ids,
      sprache: this.translate.currentLang,
      inEinemDokumentDrucken: printOptions.printOptions.inEinemDokumentDrucken,
      auszugBearbeiten: printOptions.printOptions.auszugBearbeiten,
      timePointId: this.timepointService.timepointCtx.timepointId,
    };

    return this.httpClient.post(path, zipRequest,
      {
        responseType: 'blob',
        withCredentials: this.configuration.withCredentials,
      });
  }

  private getGrundstueckPDF(grundstueckId: string): any {
    if (grundstueckId === null || grundstueckId === undefined) {
      throw new Error('Required parameter rechteversionsId was null or undefined when calling getAuszugPDF.');
    }

    let queryParameters = new HttpParams();
    const timepointCtx = this.timepointService.timepointCtx;
    if (timepointCtx.timepointId != null) {
      queryParameters = queryParameters.set('timepointId', <any>timepointCtx.timepointId);
    }

    const headers = this.defaultHeaders;

    return this.httpClient
      .get(`${this.basePath}/gb/print/grundstueckAuszugPDF/${encodeURIComponent(String(grundstueckId))}/${encodeURIComponent(String(this.translate.currentLang))}`,
        {
          params: queryParameters,
          headers: headers,
          responseType: 'blob',
          withCredentials: this.configuration.withCredentials,
        });
  }

  private getGrundstueckRTF(grundstueckId: string): any {
    if (grundstueckId === null || grundstueckId === undefined) {
      throw new Error('Required parameter request was null or undefined when calling getAuszugRTF.');
    }
    let queryParameters = new HttpParams();
    const timepointCtx = this.timepointService.timepointCtx;
    if (timepointCtx.timepointId != null) {
      queryParameters = queryParameters.set('timepointId', <any>timepointCtx.timepointId);
    }

    const headers = this.defaultHeaders;

    return this.httpClient
      .get(`${this.basePath}/gb/print/grundstueckAuszugRTF/${encodeURIComponent(String(grundstueckId))}/${encodeURIComponent(String(this.translate.currentLang))}`,
        {
          params: queryParameters,
          headers: headers,
          responseType: 'blob',
          withCredentials: this.configuration.withCredentials,
        });
  }

  public getEgvtDokument(filestoreId: string): any {
    if (filestoreId === null || filestoreId === undefined) {
      throw new Error('Required parameter filestoreId was null or undefined when calling getEgvtDokument.');
    }

    let headers = this.defaultHeaders;

    // authentication (openid) required
    if (this.configuration.accessToken) {
      const accessToken = typeof this.configuration.accessToken === 'function'
        ? this.configuration.accessToken()
        : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    // to determine the Accept header
    const httpHeaderAccepts: string[] = [
      '*/*'
    ];
    const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected !== undefined) {
      headers = headers.set("Accept", httpHeaderAcceptSelected);
    }

    return this.httpClient.get(`${this.basePath}/gb/dokulink/egvt/${encodeURIComponent(String(filestoreId))}`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        responseType: 'blob',
      }
    );
  }

  public getGrundbuchmeldungPDF(gbGeschaeftId: string): any {
    if (gbGeschaeftId === null || gbGeschaeftId === undefined) {
      throw new Error('Required parameter gbGeschaeftId was null or undefined when calling downloadGrundbuchmeldungPDF.');
    }

    let headers = this.defaultHeaders;

    // authentication (openid) required
    if (this.configuration.accessToken) {
      const accessToken = typeof this.configuration.accessToken === 'function'
        ? this.configuration.accessToken()
        : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    // to determine the Accept header
    const httpHeaderAccepts: string[] = [
      '*/*'
    ];
    const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected !== undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    return this.httpClient.get(`${this.basePath}/gb/print/grundbuchmeldungPDF/${encodeURIComponent(String(gbGeschaeftId))}/${encodeURIComponent(String(this.translate.currentLang))}`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        responseType: 'blob',
      }
    );
  }

  public getGrundbuchmeldungPDFByFachId(
    egbtbid?: string, amtNummer?: number, jahr?: number, nummer?: number, index?: number, sprache?: string, observe: any = 'body', reportProgress: boolean = false): any {

    let queryParameters = new HttpParams();
    if (egbtbid !== undefined && egbtbid !== null) {
      queryParameters = queryParameters.set('egbtbid', <any>egbtbid);
    }
    if (amtNummer !== undefined && amtNummer !== null) {
      queryParameters = queryParameters.set('amtNummer', <any>amtNummer);
    }
    if (jahr !== undefined && jahr !== null) {
      queryParameters = queryParameters.set('jahr', <any>jahr);
    }
    if (nummer !== undefined && nummer !== null) {
      queryParameters = queryParameters.set('nummer', <any>nummer);
    }
    if (index !== undefined && index !== null) {
      queryParameters = queryParameters.set('index', <any>index);
    }
    if (sprache !== undefined && sprache !== null) {
      queryParameters = queryParameters.set('sprache', <any>sprache);
    }

    let headers = this.defaultHeaders;

    // authentication (openid) required
    if (this.configuration.accessToken) {
      const accessToken = typeof this.configuration.accessToken === 'function'
        ? this.configuration.accessToken()
        : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    // to determine the Accept header
    const httpHeaderAccepts: string[] = [
      '*/*'
    ];
    const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected !== undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = [];

    return this.httpClient.get(`${this.basePath}/gb/print/grundbuchmeldungPDF`,
      {
        params: queryParameters,

        withCredentials: this.configuration.withCredentials,
        headers: headers,
        responseType: 'blob',
      }
    );
  }


  public getGrundbuchmeldungRTFByFachId(
    egbtbid?: string, amtNummer?: number, jahr?: number, nummer?: number, index?: number, sprache?: string, observe: any = 'body', reportProgress: boolean = false): any {

    let queryParameters = new HttpParams();
    if (egbtbid !== undefined && egbtbid !== null) {
      queryParameters = queryParameters.set('egbtbid', <any>egbtbid);
    }
    if (amtNummer !== undefined && amtNummer !== null) {
      queryParameters = queryParameters.set('amtNummer', <any>amtNummer);
    }
    if (jahr !== undefined && jahr !== null) {
      queryParameters = queryParameters.set('jahr', <any>jahr);
    }
    if (nummer !== undefined && nummer !== null) {
      queryParameters = queryParameters.set('nummer', <any>nummer);
    }
    if (index !== undefined && index !== null) {
      queryParameters = queryParameters.set('index', <any>index);
    }
    if (sprache !== undefined && sprache !== null) {
      queryParameters = queryParameters.set('sprache', <any>sprache);
    }

    let headers = this.defaultHeaders;

    // authentication (openid) required
    if (this.configuration.accessToken) {
      const accessToken = typeof this.configuration.accessToken === 'function'
        ? this.configuration.accessToken()
        : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    // to determine the Accept header
    const httpHeaderAccepts: string[] = [
      '*/*'
    ];
    const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected !== undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = [];

    return this.httpClient.get(`${this.basePath}/gb/print/grundbuchmeldungRTF`,
      {
        params: queryParameters,

        withCredentials: this.configuration.withCredentials,
        headers: headers,
        responseType: 'blob',
      }
    );
  }


  public getGrundbuchmeldungRTF(gbGeschaeftId: string): any {
    if (gbGeschaeftId === null || gbGeschaeftId === undefined) {
      throw new Error('Required parameter gbGeschaeftId was null or undefined when calling downloadGrundbuchmeldungRTF.');
    }

    let headers = this.defaultHeaders;

    // authentication (openid) required
    if (this.configuration.accessToken) {
      const accessToken = typeof this.configuration.accessToken === 'function'
        ? this.configuration.accessToken()
        : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    // to determine the Accept header
    const httpHeaderAccepts: string[] = [
      '*/*'
    ];
    const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected !== undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    return this.httpClient.get(`${this.configuration.basePath}/gb/print/grundbuchmeldungRTF/${encodeURIComponent(String(gbGeschaeftId))}/${encodeURIComponent(String(this.translate.currentLang))}`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        responseType: 'blob',
      }
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
